import React, { useState, useEffect, useMemo, useCallback } from 'react'
import ReactDOM from 'react-dom'
import { backendService } from '../../services/backendService'
import type {
  TemplateTraceResponse,
  RefineParams,
  ShellParams,
} from '../../services/types/insoleV2'
import {
  formatAlignError,
  isCalibrationRequiredError,
} from '../../services/api/insoleV2Api'
import { logInfo, logError } from '../../utils/errorLogger'
import './TemplateUploadDialog.css'

// Template item shape from listTemplates
interface TemplateItem {
  id: string
  name: string
  size?: string
  type?: string
  is_shared?: boolean
  created_by?: string
}

// Alignment result passed back to parent
export interface AlignResult {
  session_id: string
  alignment_id: string
  aligned_template_2d: number[][]
  plantar_projection: number[][] | null
  confidence_score: number | null
}

export interface TemplateUploadDialogProps {
  isOpen: boolean
  onClose: () => void
  doctorId: string
  // V2 Pipeline mode props
  v2Mode?: boolean
  onTemplateSelected?: (templateId: string) => void
  onAlignmentComplete?: (result: AlignResult) => void
  onGenerateRequested?: (
    alignResult: AlignResult,
    adjustTranslation: [number, number],
    adjustRotation: number,
    adjustScale: number,
    refine?: RefineParams,
    shell?: ShellParams
  ) => void
  /**
   * Live preview hook: fires while the user is on the align-preview step
   * with the currently displayed (translated/rotated/scaled) 2D template
   * points so the parent can render them as an overlay in the main 3D
   * canvas. Receives `null` when the preview should be cleared (dialog
   * closed, step changed away, or alignment reset).
   */
  onAdjustPreview?: (
    preview: {
      points: number[][]
      name?: string
      originCentroid?: [number, number]
      zOffset?: number
    } | null
  ) => void
  /**
   * Fires whenever the user picks a different size/gender/type combination,
   * so the parent (GirasolesView) can surface the selected insole template
   * characteristics in the G-Object Manager + Insole Characteristics card.
   * Receives `null` when no template is currently picked.
   */
  onTemplateSelectionChange?: (
    selection:
      | {
          templateId: string
          templateName: string
          size: string
          gender: string
          type: string
        }
      | null
  ) => void
  /**
   * Optional DOM container the alignment-step side panel should be portalled
   * into so it can be pinned to the right edge of the 3D canvas (rather
   * than the full viewport, which would overlap the global Admin Panel
   * button). When omitted, the dialog falls back to a full-viewport modal.
   */
  pinToContainer?: HTMLElement | null
  currentStlFileId?: string
  projectId?: string
}

type V2Step = 'template' | 'upload' | 'upload-preview' | 'align-preview' | 'generating'

export const TemplateUploadDialog: React.FC<TemplateUploadDialogProps> = ({
  isOpen,
  onClose,
  v2Mode = false,
  onTemplateSelected,
  onAlignmentComplete,
  onGenerateRequested,
  onAdjustPreview,
  onTemplateSelectionChange,
  pinToContainer,
  currentStlFileId,
  projectId,
}) => {
  // Template list state
  const [templates, setTemplates] = useState<TemplateItem[]>([])
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false)
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)

  // Upload form state
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [uploadName, setUploadName] = useState('')
  const [uploadSizeEu, setUploadSizeEu] = useState(42)
  const [uploadGender, setUploadGender] = useState<'male' | 'female' | 'pediatric'>('male')
  const [uploadType, setUploadType] = useState<'full_length' | 'three_quarter' | 'metatarsal' | 'heel_cup'>('full_length')
  const [uploadIsShared, setUploadIsShared] = useState(false)
  const [uploadPreviewUrl, setUploadPreviewUrl] = useState<string | null>(null)
  const [isUploadingTemplate, setIsUploadingTemplate] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null)
  const [uploadPreviewData, setUploadPreviewData] = useState<TemplateTraceResponse | null>(null)

  // V2 multi-step state
  const [v2Step, setV2Step] = useState<V2Step>('template')

  // Width-adjustable side panel (alignment step). Persisted to localStorage.
  const ALIGN_PANEL_KEY = 'gv.v2.alignPanel.w'
  // Default panel width is 280 (≈ 2/3 of the previous 420). Persisted user
  // override is honoured if it has been saved previously.
  const ALIGN_PANEL_DEFAULT_W = 280
  const [alignPanelWidth, setAlignPanelWidth] = useState<number>(() => {
    try {
      const saved = localStorage.getItem(ALIGN_PANEL_KEY)
      const n = saved ? parseInt(saved, 10) : NaN
      return !isNaN(n) ? n : ALIGN_PANEL_DEFAULT_W
    } catch {
      return ALIGN_PANEL_DEFAULT_W
    }
  })
  const [isResizingAlignPanel, setIsResizingAlignPanel] = useState(false)
  const alignResizeStartX = React.useRef(0)
  const alignResizeStartW = React.useRef(0)
  const handleAlignResizeStart = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    alignResizeStartX.current = e.clientX
    alignResizeStartW.current = alignPanelWidth
    setIsResizingAlignPanel(true)
  }
  useEffect(() => {
    if (!isResizingAlignPanel) return
    document.body.classList.add('resizing-panel')
    const onMove = (ev: MouseEvent) => {
      const dx = ev.clientX - alignResizeStartX.current
      // Panel is anchored on the RIGHT; dragging the left-edge handle
      // LEFT (dx < 0) GROWS the panel, hence the minus.
      const next = Math.max(240, Math.min(720, alignResizeStartW.current - dx))
      setAlignPanelWidth(next)
    }
    const onUp = () => {
      setIsResizingAlignPanel(false)
      document.body.classList.remove('resizing-panel')
      try { localStorage.setItem(ALIGN_PANEL_KEY, String(alignPanelWidth)) } catch { /* ignore */ }
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      document.body.classList.remove('resizing-panel')
    }
  }, [isResizingAlignPanel, alignPanelWidth])

  // Alignment state
  const [alignResult, setAlignResult] = useState<AlignResult | null>(null)
  const [isAligning, setIsAligning] = useState(false)
  const [alignError, setAlignError] = useState<string | null>(null)
  const [generateError, setGenerateError] = useState<string | null>(null)
  const [adjustTranslation, setAdjustTranslation] = useState<[number, number]>([0, 0])
  const [adjustTranslationZ, setAdjustTranslationZ] = useState<number>(0)
  const [adjustRotation, setAdjustRotation] = useState(0)
  const [adjustScale, setAdjustScale] = useState(1.0)

  // Listen for TransformControls gizmo deltas dispatched from Canvas3D via
  // GirasolesView. Apply absolute deltas (gizmo proxy resets to origin on
  // each drag), accumulating into the slider state.
  useEffect(() => {
    if (v2Step !== 'align-preview') return
    const baseRef = { tx: 0, ty: 0, tz: 0, rot: 0, scale: 1 }
    const onStart = () => {
      baseRef.tx = adjustTranslation[0]
      baseRef.ty = adjustTranslation[1]
      baseRef.tz = adjustTranslationZ
      baseRef.rot = adjustRotation
      baseRef.scale = adjustScale
    }
    // Capture current values when the dialog mounts/aligns; refresh on each event start
    onStart()
    const onTransform = (e: Event) => {
      const detail = (e as CustomEvent).detail as { dx: number; dy: number; dz?: number; drot: number; dscale: number }
      setAdjustTranslation([baseRef.tx + detail.dx, baseRef.ty + detail.dy])
      if (typeof detail.dz === 'number' && detail.dz !== 0) {
        setAdjustTranslationZ(baseRef.tz + detail.dz)
      }
      const degDelta = (detail.drot * 180) / Math.PI
      setAdjustRotation(baseRef.rot + degDelta)
      setAdjustScale(Math.max(0.5, Math.min(2.0, baseRef.scale * (1 + detail.dscale))))
    }
    const onEnd = () => {
      // Re-read current state for next drag
      onStart()
    }
    window.addEventListener('gv:v2-template-transform', onTransform as EventListener)
    window.addEventListener('gv:v2-template-transform-end', onEnd as EventListener)
    return () => {
      window.removeEventListener('gv:v2-template-transform', onTransform as EventListener)
      window.removeEventListener('gv:v2-template-transform-end', onEnd as EventListener)
    }
  }, [v2Step, adjustTranslation, adjustTranslationZ, adjustRotation, adjustScale])

  // V2 refine (shell-rebuild) advanced parameters
  const [refineEnabled, setRefineEnabled] = useState(true)
  const [refineGridStep, setRefineGridStep] = useState(1.0)
  const [refineThickness, setRefineThickness] = useState(3.0)
  const [refineRimFillet, setRefineRimFillet] = useState(2.0)
  const [refineHullRatio, setRefineHullRatio] = useState(0.35)
  const [refineBottomFlat, setRefineBottomFlat] = useState(false)

  // Align option: auto-fit template to detected foot outline.
  // Backend PR #414 accepts snap_to_boundary as a non-rigid pull.
  const [snapToBoundary, setSnapToBoundary] = useState(false)

  // Corrective shell parameters (backend ShellParams). Defaults match
  // models.insole_v2.ShellParams.
  const [shellEnabled, setShellEnabled] = useState(true)
  const [shellHeelCup, setShellHeelCup] = useState(true)
  const [shellRimScale, setShellRimScale] = useState(1.0)
  const [shellArchDome, setShellArchDome] = useState(8.0)
  const [shellMedialWall, setShellMedialWall] = useState(8.0)
  const [shellHeelSupport, setShellHeelSupport] = useState(0.0)

  // Template editing state
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null)
  const [editingTemplateName, setEditingTemplateName] = useState('')
  const [isDeletingTemplateId, setIsDeletingTemplateId] = useState<string | null>(null)

  // Computed: real-time alignment preview with client-side adjustments
  const displayedTemplatePoints = useMemo(() => {
    const pts = alignResult?.aligned_template_2d
    if (!pts || pts.length === 0) return []
    const [tx, ty] = adjustTranslation
    if (tx === 0 && ty === 0 && adjustRotation === 0 && adjustScale === 1.0) return pts
    const cx = pts.reduce((s: number, p: number[]) => s + p[0], 0) / pts.length
    const cy = pts.reduce((s: number, p: number[]) => s + p[1], 0) / pts.length
    const rotRad = (adjustRotation * Math.PI) / 180
    const cos = Math.cos(rotRad)
    const sin = Math.sin(rotRad)
    return (pts as [number, number][]).map(([x, y]) => {
      const dx = x - cx, dy = y - cy
      const sx = dx * adjustScale, sy = dy * adjustScale
      return [sx * cos - sy * sin + cx + tx, sx * sin + sy * cos + cy + ty] as [number, number]
    })
  }, [alignResult, adjustTranslation, adjustRotation, adjustScale])

  // Live overlay preview into the main 3D canvas. Fires whenever the
  // displayed (translated/rotated/scaled) template points change while we
  // are on the alignment step. Cleared on close, step change, or unmount.
  // Parent's onAdjustPreview is captured via a ref so changes to its
  // identity don't retrigger the effect (avoids update loops).
  const onAdjustPreviewRef = React.useRef(onAdjustPreview)
  useEffect(() => {
    onAdjustPreviewRef.current = onAdjustPreview
  }, [onAdjustPreview])
  useEffect(() => {
    const cb = onAdjustPreviewRef.current
    if (!cb) return
    if (v2Step !== 'align-preview' || !alignResult) {
      cb(null)
      return
    }
    const tpl = templates.find(t => t.id === selectedTemplateId)
    // Compute the centroid of the ORIGINAL aligned points (pre slider
    // adjustment) so Canvas3D can center against a stable anchor and
    // user-applied Translate X/Y is not cancelled by re-centering on
    // the moved points.
    const orig = alignResult.aligned_template_2d
    let originCentroid: [number, number] | undefined
    if (orig && orig.length > 0) {
      let sx = 0, sy = 0
      for (const p of orig) { sx += p[0]; sy += p[1] }
      originCentroid = [sx / orig.length, sy / orig.length]
    }
    cb({
      points: displayedTemplatePoints,
      name: tpl?.name || 'Aligned template',
      originCentroid,
      zOffset: adjustTranslationZ,
    })
  }, [
    v2Step,
    alignResult,
    displayedTemplatePoints,
    selectedTemplateId,
    templates,
    adjustTranslationZ,
  ])

  // Always clear overlay on unmount.
  useEffect(() => {
    return () => {
      onAdjustPreviewRef.current?.(null)
    }
  }, [])

  // Clear template-selection metadata when the dialog closes so the
  // Insole Cut row + Insole Characteristics card disappear with it.
  const onTemplateSelectionChangeRef = React.useRef(onTemplateSelectionChange)
  useEffect(() => {
    onTemplateSelectionChangeRef.current = onTemplateSelectionChange
  }, [onTemplateSelectionChange])
  useEffect(() => {
    if (!isOpen) {
      onTemplateSelectionChangeRef.current?.(null)
    }
  }, [isOpen])
  useEffect(() => {
    return () => {
      onTemplateSelectionChangeRef.current?.(null)
    }
  }, [])

  // Reset upload form fields
  const resetUploadFields = useCallback(() => {
    setUploadFile(null)
    setUploadName('')
    setUploadSizeEu(42)
    setUploadGender('male')
    setUploadType('full_length')
    setUploadIsShared(false)
    setUploadError(null)
    setUploadSuccess(null)
    setUploadPreviewData(null)
    if (uploadPreviewUrl) {
      URL.revokeObjectURL(uploadPreviewUrl)
      setUploadPreviewUrl(null)
    }
  }, [uploadPreviewUrl])

  // Reset all V2 pipeline state
  const resetAllState = useCallback(() => {
    setV2Step('template')
    setAlignResult(null)
    setIsAligning(false)
    setAlignError(null)
    setGenerateError(null)
    setAdjustTranslation([0, 0])
    setAdjustTranslationZ(0)
    setAdjustRotation(0)
    setAdjustScale(1.0)
    setSelectedTemplateId(null)
    setUploadPreviewData(null)
    resetUploadFields()
    setEditingTemplateId(null)
    setEditingTemplateName('')
    setIsDeletingTemplateId(null)
    setSnapToBoundary(false)
    // Shell params are intentionally not reset — doctor preferences carry
    // over across templates within the same dialog session.
  }, [resetUploadFields])

  // Load templates when dialog opens
  useEffect(() => {
    if (!isOpen) return
    setIsLoadingTemplates(true)
    backendService.listTemplates(1, 100).then(res => {
      setTemplates(res.templates)
      setIsLoadingTemplates(false)
    }).catch(err => {
      logError(`Failed to load templates: ${err}`, 'TemplateUploadDialog')
      setTemplates([])
      setIsLoadingTemplates(false)
    })
  }, [isOpen])

  // Reset state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      resetAllState()
    }
  }, [isOpen, resetAllState])

  // Reload templates helper
  const reloadTemplates = async () => {
    try {
      const res = await backendService.listTemplates(1, 100)
      setTemplates(res.templates)
    } catch (err) {
      logError(`Failed to reload templates: ${err}`, 'TemplateUploadDialog')
    }
  }

  // Handle closing
  const handleClose = () => {
    if (isUploadingTemplate || isAligning) return
    resetAllState()
    onClose()
  }

  // ==================== TEMPLATE MANAGEMENT ====================

  // Delete a template
  const handleDeleteTemplate = async (templateId: string) => {
    setIsDeletingTemplateId(templateId)
    try {
      await backendService.deleteTemplate(templateId)
      await reloadTemplates()
      if (selectedTemplateId === templateId) {
        setSelectedTemplateId(null)
      }
      logInfo(`Template ${templateId} deleted`, 'TemplateUploadDialog')
    } catch (err: any) {
      logError(`Failed to delete template: ${err.message}`, 'TemplateUploadDialog')
      setUploadError(`Failed to delete template: ${err.message}`)
    } finally {
      setIsDeletingTemplateId(null)
    }
  }

  // Start editing a template name
  const handleStartEdit = (template: TemplateItem) => {
    setEditingTemplateId(template.id)
    setEditingTemplateName(template.name)
  }

  // Confirm template rename (re-upload with same ID is not supported, so we rename via backend)
  const handleConfirmEdit = async (templateId: string) => {
    if (!editingTemplateName.trim()) {
      setEditingTemplateId(null)
      return
    }
    // The backend does not have a dedicated rename endpoint, so we handle this
    // by showing the new name locally. If a rename API is added later, use it here.
    // For now, we update the local state only.
    setTemplates(prev => prev.map(t =>
      t.id === templateId ? { ...t, name: editingTemplateName.trim() } : t
    ))
    setEditingTemplateId(null)
    setEditingTemplateName('')
  }

  // ==================== UPLOAD HANDLERS ====================

  // Simple upload (toolbar mode) - Upload & Trace in a single step
  const handleUploadTemplate = async () => {
    if (!uploadFile) return
    setIsUploadingTemplate(true)
    setUploadError(null)
    setUploadSuccess(null)
    try {
      const result = await backendService.traceTemplate({
        file: uploadFile,
        size_eu: uploadSizeEu,
        gender: uploadGender,
        type: uploadType,
        name: uploadName || uploadFile.name.replace(/\.\w+$/, ''),
        is_shared: uploadIsShared,
        save: true,
      })
      setUploadSuccess(`Template "${result.name}" saved successfully${result.id ? ` (ID: ${result.id})` : ''}`)
      setUploadFile(null)
      setUploadName('')
      if (uploadPreviewUrl) {
        URL.revokeObjectURL(uploadPreviewUrl)
        setUploadPreviewUrl(null)
      }
      await reloadTemplates()
    } catch (err: any) {
      setUploadError(err.message || 'Upload failed')
    } finally {
      setIsUploadingTemplate(false)
    }
  }

  // V2 upload next: trace (save if SVG) or show preview (JPG/PNG)
  const handleV2UploadNext = async () => {
    if (!uploadFile) return
    const isSvg = uploadFile.type === 'image/svg+xml' || uploadFile.name.endsWith('.svg')
    setIsUploadingTemplate(true)
    setUploadError(null)
    try {
      const result = await backendService.traceTemplate({
        file: uploadFile,
        size_eu: uploadSizeEu,
        gender: uploadGender,
        type: uploadType,
        name: uploadName || uploadFile.name.replace(/\.\.+$/, ''),
        is_shared: uploadIsShared,
        save: isSvg,
      })
      if (isSvg) {
        await reloadTemplates()
        if (result.id) setSelectedTemplateId(result.id)
        resetUploadFields()
        setV2Step('template')
      } else {
        setUploadPreviewData(result)
        setV2Step('upload-preview')
      }
    } catch (err: any) {
      setUploadError(err.message || 'Upload failed')
    } finally {
      setIsUploadingTemplate(false)
    }
  }

  // V2 confirm traced preview and save
  const handleV2UploadConfirm = async () => {
    if (!uploadFile) return
    setIsUploadingTemplate(true)
    setUploadError(null)
    try {
      const result = await backendService.traceTemplate({
        file: uploadFile,
        size_eu: uploadSizeEu,
        gender: uploadGender,
        type: uploadType,
        name: uploadName || uploadFile.name.replace(/\.\.+$/, ''),
        is_shared: uploadIsShared,
        save: true,
      })
      await reloadTemplates()
      if (result.id) setSelectedTemplateId(result.id)
      resetUploadFields()
      setV2Step('template')
    } catch (err: any) {
      setUploadError(err.message || 'Save failed')
    } finally {
      setIsUploadingTemplate(false)
    }
  }

  // ==================== V2 ALIGNMENT ====================

  const handleV2Align = async (templateId: string) => {
    if (!projectId || !currentStlFileId) return
    setIsAligning(true)
    setAlignError(null)
    try {
      const result = await backendService.alignInsole({
        stl_file_id: currentStlFileId,
        template_id: templateId,
        snap_to_boundary: snapToBoundary,
      })
      const ar: AlignResult = {
        session_id: result.session_id,
        alignment_id: result.alignment_id,
        aligned_template_2d: result.aligned_template_2d,
        plantar_projection: result.plantar_projection,
        confidence_score: result.confidence_score,
      }
      setAlignResult(ar)
      setV2Step('align-preview')
      onAlignmentComplete?.(ar)
      logInfo(`V2 alignment complete. Session: ${result.session_id}, confidence: ${result.confidence_score}`, 'TemplateUploadDialog')
    } catch (err) {
      const msg = formatAlignError(err)
      setAlignError(msg)
      // Surface the "open STL Input to calibrate" path when the scan is
      // uncalibrated. formatAlignError already produced a descriptive
      // message; the calibration-required flag is logged for QA triage.
      if (isCalibrationRequiredError(err)) {
        logError(
          `V2 alignment blocked: scan not calibrated (heel/MT1/MT5 required).`,
          'TemplateUploadDialog'
        )
      } else {
        logError(`V2 alignment error: ${msg}`, 'TemplateUploadDialog')
      }
    } finally {
      setIsAligning(false)
    }
  }

  const handleV2Generate = () => {
    if (!alignResult) return
    const refine: RefineParams = {
      enabled: refineEnabled,
      grid_step_mm: refineGridStep,
      thickness_mm: refineThickness,
      rim_fillet_mm: refineRimFillet,
      hull_ratio: refineHullRatio,
      bottom_flat: refineBottomFlat,
    }
    const shell: ShellParams = {
      enabled: shellEnabled,
      heel_cup: shellHeelCup,
      rim_scale: shellRimScale,
      arch_dome_height_mm: shellArchDome,
      medial_wall_height_mm: shellMedialWall,
      heel_support_mm: shellHeelSupport,
    }
    onGenerateRequested?.(
      alignResult,
      adjustTranslation,
      adjustRotation,
      adjustScale,
      refine,
      shell
    )
  }

  // ==================== RENDERING ====================

  if (!isOpen) return null

  // Shared template list with edit/delete buttons
  const renderTemplateList = (selectable: boolean) => {
    const userTemplates = templates.filter(t => t.created_by)
    if (templates.length === 0 && !isLoadingTemplates) {
      return (
        <div className="my-templates-section">
          <label>Your Templates</label>
          <span className="no-templates-hint">No templates uploaded yet</span>
        </div>
      )
    }

    const listItems = selectable ? templates : userTemplates

    if (listItems.length === 0) return null

    return (
      <div className="my-templates-section">
        <label>Your Templates</label>
        <div className="my-templates-list">
          {listItems.map(t => (
            <div
              key={t.id}
              className={`my-template-item ${selectable && selectedTemplateId === t.id ? 'selected' : ''}`}
              onClick={selectable ? () => setSelectedTemplateId(t.id) : undefined}
              role={selectable ? 'button' : undefined}
              tabIndex={selectable ? 0 : undefined}
            >
              {editingTemplateId === t.id ? (
                <input
                  className="template-edit-input"
                  type="text"
                  value={editingTemplateName}
                  onChange={e => setEditingTemplateName(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleConfirmEdit(t.id)
                    if (e.key === 'Escape') setEditingTemplateId(null)
                  }}
                  onBlur={() => handleConfirmEdit(t.id)}
                  autoFocus
                  onClick={e => e.stopPropagation()}
                />
              ) : (
                <span className="template-name">{t.name}</span>
              )}
              {t.size && <span className="template-size">EU {t.size}</span>}
              {t.type && <span className="template-type">{t.type}</span>}
              <span className="template-visibility">{t.is_shared ? 'Shared' : 'Private'}</span>
              <div className="template-actions" onClick={e => e.stopPropagation()}>
                {editingTemplateId === t.id ? (
                  <button
                    className="template-action-btn save-btn"
                    onClick={() => handleConfirmEdit(t.id)}
                    title="Save"
                  >
                    Save
                  </button>
                ) : (
                  <button
                    className="template-action-btn edit-btn"
                    onClick={() => handleStartEdit(t)}
                    title="Rename template"
                  >
                    Edit
                  </button>
                )}
                <button
                  className="template-action-btn delete-btn"
                  onClick={() => {
                    if (window.confirm(`Delete template "${t.name}"? This cannot be undone.`)) {
                      handleDeleteTemplate(t.id)
                    }
                  }}
                  disabled={isDeletingTemplateId === t.id}
                  title="Delete template"
                >
                  {isDeletingTemplateId === t.id ? '...' : 'Delete'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  // Upload form (shared between simple and v2 modes)
  const renderUploadForm = () => (
    <>
      {uploadError && (
        <div className="v2-error-banner">
          <span>{uploadError}</span>
          <button onClick={() => setUploadError(null)}>Dismiss</button>
        </div>
      )}
      {uploadSuccess && (
        <div className="upload-success">{uploadSuccess}</div>
      )}

      <div className="form-group">
        <label>Template File (JPG/PNG/SVG)</label>
        <input
          type="file"
          accept="image/jpeg,image/png,image/svg+xml,.svg"
          onChange={(e) => {
            const file = e.target.files?.[0] || null
            setUploadFile(file)
            if (uploadPreviewUrl) URL.revokeObjectURL(uploadPreviewUrl)
            setUploadPreviewUrl(file ? URL.createObjectURL(file) : null)
          }}
          disabled={isUploadingTemplate}
        />
      </div>

      {/* File preview (for simple mode) */}
      {!v2Mode && uploadPreviewUrl && (
        <div className="upload-preview">
          <img src={uploadPreviewUrl} alt="Template preview" />
        </div>
      )}

      <div className="form-group">
        <label>Template Name</label>
        <input
          type="text"
          value={uploadName}
          onChange={(e) => setUploadName(e.target.value)}
          placeholder="e.g. Custom Template 42M"
          disabled={isUploadingTemplate}
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>EU Size</label>
          <input
            type="number"
            min={30}
            max={50}
            step={0.5}
            value={uploadSizeEu}
            onChange={(e) => setUploadSizeEu(parseFloat(e.target.value))}
            disabled={isUploadingTemplate}
          />
        </div>
        <div className="form-group">
          <label>Gender</label>
          <select
            value={uploadGender}
            onChange={(e) => setUploadGender(e.target.value as any)}
            disabled={isUploadingTemplate}
          >
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="pediatric">Pediatric</option>
          </select>
        </div>
        <div className="form-group">
          <label>Type</label>
          <select
            value={uploadType}
            onChange={(e) => setUploadType(e.target.value as any)}
            disabled={isUploadingTemplate}
          >
            <option value="full_length">Full Length</option>
            <option value="three_quarter">3/4 Length</option>
            <option value="metatarsal">Metatarsal</option>
            <option value="heel_cup">Heel Cup</option>
          </select>
        </div>
      </div>

      <div className="form-group">
        <label>Visibility</label>
        <select
          value={uploadIsShared ? 'public' : 'private'}
          onChange={(e) => setUploadIsShared(e.target.value === 'public')}
          disabled={isUploadingTemplate}
        >
          <option value="private">🔒 Private — only you</option>
          <option value="public">🌐 Public — visible to everyone</option>
        </select>
      </div>
    </>
  )

  // ==================== SIMPLE (TOOLBAR) MODE ====================

  if (!v2Mode) {
    return (
      <div className="modal-overlay" onClick={handleClose}>
        <div className="modal-content v2-pipeline-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>Upload New Template</h3>
            <button className="modal-close" onClick={handleClose} disabled={isUploadingTemplate}>x</button>
          </div>
          <div className="modal-body">
            {renderUploadForm()}
            {renderTemplateList(false)}
          </div>
          <div className="modal-footer">
            <button
              className="btn-cancel"
              onClick={handleClose}
              disabled={isUploadingTemplate}
            >
              Cancel
            </button>
            <button
              className="btn-primary"
              onClick={handleUploadTemplate}
              disabled={isUploadingTemplate || !uploadFile}
            >
              {isUploadingTemplate ? 'Uploading...' : 'Upload & Trace'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ==================== V2 PIPELINE MODE ====================

  // On the alignment-preview step we render as a non-blocking side panel
  // anchored to the LEFT edge so the user can interact with the 3D canvas
  // (orbit, zoom) while tweaking translate / rotate / scale and watching
  // the cyan template overlay update live.
  const isAlignSidePanel = v2Step === 'align-preview'
  // When pinToContainer is provided AND we're on the side-panel step, render
  // via a portal so the panel stays inside the canvas-area DOM node and
  // never overlaps the global Admin Panel button at the top of the page.
  const usePortal = isAlignSidePanel && !!pinToContainer

  const dialogTree = (
    <div
      className="modal-overlay"
      onClick={() => !isUploadingTemplate && !isAligning && handleClose()}
      style={
        isAlignSidePanel
          ? usePortal
            ? {
                position: 'absolute',
                inset: 0,
                background: 'transparent',
                pointerEvents: 'none',
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'stretch',
                padding: 0,
                zIndex: 50,
              }
            : {
                background: 'transparent',
                pointerEvents: 'none',
                justifyContent: 'flex-end',
                alignItems: 'flex-start',
                padding: 0,
              }
          : undefined
      }
    >
      <div
        className="modal-content v2-pipeline-modal"
        onClick={(e) => e.stopPropagation()}
        style={
          isAlignSidePanel
            ? {
                pointerEvents: 'auto',
                maxWidth: alignPanelWidth,
                width: alignPanelWidth,
                // Without an explicit minWidth the flex parent uses the
                // intrinsic content size (≈ 400px), defeating the smaller
                // user-chosen widths.
                minWidth: alignPanelWidth,
                marginTop: 0,
                maxHeight: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                borderRadius: 0,
              }
            : undefined
        }
      >
        <div className="modal-header">
          <h3>
            {v2Step === 'template' && 'V2 Pipeline -- Select Template'}
            {v2Step === 'upload' && 'V2 Pipeline -- Upload New Template'}
            {v2Step === 'upload-preview' && 'V2 Pipeline -- Preview Traced Outline'}
            {v2Step === 'align-preview' && 'V2 Pipeline -- Adjust Template Alignment'}
            {v2Step === 'generating' && 'V2 Pipeline -- Generating...'}
          </h3>
          <button className="modal-close" onClick={handleClose}>x</button>
        </div>
        {isAlignSidePanel && (
          <div
            onMouseDown={handleAlignResizeStart}
            title="Drag to resize panel"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              bottom: 0,
              width: 6,
              cursor: 'ew-resize',
              background: isResizingAlignPanel ? 'rgba(0,122,204,0.25)' : 'transparent',
              zIndex: 10,
            }}
          />
        )}

        {/* Step 1: Template Selection (size / gender / type dropdowns) */}
        {v2Step === 'template' && (
          <>
            <div className="modal-body">
              {isLoadingTemplates ? (
                <p>Loading templates...</p>
              ) : templates.length === 0 ? (
                <p>No templates available.</p>
              ) : (
                <V2TemplatePicker
                  templates={templates}
                  selectedTemplateId={selectedTemplateId}
                  onSelect={setSelectedTemplateId}
                  onSelectionChange={onTemplateSelectionChange}
                />
              )}
              {/* Align option: auto-fit template edge to detected foot outline */}
              <div
                className="v2-adjust-row"
                style={{ marginTop: 12, alignItems: 'flex-start' }}
              >
                <label
                  title="Non-rigidly pulls the template edge toward the detected foot outline (bounded)."
                  style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}
                >
                  <input
                    type="checkbox"
                    data-testid="v2-snap-to-boundary"
                    checked={snapToBoundary}
                    onChange={(e) => setSnapToBoundary(e.target.checked)}
                  />
                  <span>
                    Auto-fit template to foot outline
                    <span style={{ display: 'block', fontSize: 12, color: '#64748b', marginTop: 2 }}>
                      Non-rigidly pulls the template edge toward the detected foot
                      outline (bounded). Off by default.
                    </span>
                  </span>
                </label>
              </div>

              {alignError && (
                <div className="v2-error-banner">
                  <span>Alignment failed: {alignError}</span>
                  <button onClick={() => setAlignError(null)}>Dismiss</button>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={handleClose}>Cancel</button>
              <button
                className="btn-primary"
                disabled={!selectedTemplateId || isAligning}
                onClick={() => {
                  if (selectedTemplateId) {
                    onTemplateSelected?.(selectedTemplateId)
                    handleV2Align(selectedTemplateId)
                  }
                }}
              >
                {isAligning ? 'Aligning...' : 'Align Template'}
              </button>
            </div>
          </>
        )}

        {/* Upload Sub-Step */}
        {v2Step === 'upload' && (
          <>
            <div className="modal-body">
              {renderUploadForm()}
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => { resetUploadFields(); setV2Step('template') }}>Back</button>
              {uploadFile && (uploadFile.type === 'image/svg+xml' || uploadFile.name.endsWith('.svg')) ? (
                <button
                  className="btn-primary"
                  disabled={isUploadingTemplate || !uploadFile}
                  onClick={handleV2UploadNext}
                >
                  {isUploadingTemplate ? 'Uploading...' : 'Upload & Save'}
                </button>
              ) : (
                <button
                  className="btn-primary"
                  disabled={isUploadingTemplate || !uploadFile}
                  onClick={handleV2UploadNext}
                >
                  {isUploadingTemplate ? 'Tracing...' : 'Next'}
                </button>
              )}
            </div>
          </>
        )}

        {/* Upload Preview Sub-Step (JPG/PNG only) */}
        {v2Step === 'upload-preview' && uploadPreviewData && (
          <>
            <div className="modal-body">
              {uploadError && (
                <div className="v2-error-banner">
                  <span>{uploadError}</span>
                  <button onClick={() => setUploadError(null)}>Dismiss</button>
                </div>
              )}
              <div className="v2-upload-preview">
                <div className="v2-upload-preview-svg">
                  {uploadPreviewData.outline_svg ? (
                    <svg
                      viewBox={(() => {
                        const pts = uploadPreviewData.outline_points || []
                        if (pts.length === 0) return "0 0 300 400"
                        const xs = pts.map(p => p[0])
                        const ys = pts.map(p => p[1])
                        const margin = 10
                        const minX = Math.min(...xs) - margin
                        const minY = Math.min(...ys) - margin
                        const w = Math.max(...xs) - Math.min(...xs) + 2 * margin
                        const h = Math.max(...ys) - Math.min(...ys) + 2 * margin
                        return `${minX} ${minY} ${w} ${h}`
                      })()}
                      className="v2-upload-preview-outline"
                      preserveAspectRatio="xMidYMid meet"
                    >
                      <path d={uploadPreviewData.outline_svg} fill="none" stroke="#22d3ee" strokeWidth="1.5" />
                    </svg>
                  ) : (
                    <p>No outline SVG available</p>
                  )}
                </div>
                {uploadPreviewData.metadata && (
                  <div className="v2-upload-preview-info">
                    Traced dimensions: {uploadPreviewData.metadata.traced_width_mm?.toFixed(1) ?? '?'} x {uploadPreviewData.metadata.traced_height_mm?.toFixed(1) ?? '?'} mm
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => { setUploadPreviewData(null); setV2Step('upload') }}>Back</button>
              <button
                className="btn-primary"
                disabled={isUploadingTemplate}
                onClick={handleV2UploadConfirm}
              >
                {isUploadingTemplate ? 'Saving...' : 'Confirm & Save'}
              </button>
            </div>
          </>
        )}

        {/* Step 2: Alignment Preview & Adjustment */}
        {v2Step === 'align-preview' && alignResult && (
          <>
            <div className="modal-body v2-align-preview">
              {/* Preview is rendered in the main 3D Girasoles canvas. The
                  cyan outline updates live as the user tweaks translate /
                  rotate / scale below. */}
              <div
                style={{
                  padding: '10px 14px',
                  borderRadius: 6,
                  background: '#eff6ff',
                  border: '1px solid #bfdbfe',
                  color: '#1e3a8a',
                  fontSize: 13,
                  marginBottom: 8,
                }}
              >
                Preview is shown directly on the foot in the main 3D view.
                Use the controls below — including <strong>Rotation</strong>{' '}
                — to fit the cut to the foot model. Drag to orbit the
                canvas; the overlay updates live.
              </div>

              {/* Confidence score */}
              <div className="v2-confidence">
                <span>Confidence: </span>
                <span className={`v2-confidence-score ${(alignResult.confidence_score ?? 0) < 0.5 ? 'low' : (alignResult.confidence_score ?? 0) < 0.8 ? 'medium' : 'high'}`}>
                  {alignResult.confidence_score != null ? `${(alignResult.confidence_score * 100).toFixed(0)}%` : 'N/A'}
                </span>
                {(alignResult.confidence_score ?? 0) < 0.5 && (
                  <span className="v2-confidence-warning"> -- Low confidence. Consider adjusting.</span>
                )}
              </div>

              {/* Adjustment controls */}
              <div className="v2-adjust-controls">
                <div className="v2-adjust-row">
                  <label>Translate X (mm):</label>
                  <input
                    type="number"
                    step="1"
                    value={adjustTranslation[0]}
                    onChange={(e) => setAdjustTranslation([Number(e.target.value), adjustTranslation[1]])}
                  />
                </div>
                <div className="v2-adjust-row">
                  <label>Translate Y (mm):</label>
                  <input
                    type="number"
                    step="1"
                    value={adjustTranslation[1]}
                    onChange={(e) => setAdjustTranslation([adjustTranslation[0], Number(e.target.value)])}
                  />
                </div>
                <div className="v2-adjust-row">
                  <label>Translate Z (mm):</label>
                  <input
                    type="number"
                    step="0.5"
                    value={adjustTranslationZ}
                    onChange={(e) => setAdjustTranslationZ(Number(e.target.value))}
                  />
                </div>
                <div className="v2-adjust-row">
                  <label>Rotation (deg):</label>
                  <input
                    type="number"
                    step="1"
                    min={-45}
                    max={45}
                    value={adjustRotation}
                    onChange={(e) => setAdjustRotation(Number(e.target.value))}
                  />
                </div>
                <div className="v2-adjust-row">
                  <label>Scale:</label>
                  <input
                    type="number"
                    step="0.05"
                    min={0.5}
                    max={2.0}
                    value={adjustScale}
                    onChange={(e) => setAdjustScale(Number(e.target.value))}
                  />
                </div>
              </div>

              {alignError && (
                <div className="v2-error-banner">
                  <span>{alignError}</span>
                  <button onClick={() => setAlignError(null)}>Dismiss</button>
                </div>
              )}
              {generateError && (
                <div className="v2-error-banner">
                  <span>Generation failed: {generateError}</span>
                  <button onClick={() => setGenerateError(null)}>Dismiss</button>
                </div>
              )}

              {/* Corrective shell (heel cup / arch dome / medial wall / heel support) */}
              <details
                className="v2-refine-advanced"
                style={{ marginTop: 12 }}
                open
              >
                <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
                  Corrective shell
                </summary>
                <div className="v2-adjust-controls" style={{ marginTop: 8 }}>
                  <div className="v2-adjust-row">
                    <label
                      title="Master switch. When off, the base draped shell is generated without designed orthotic relief."
                    >
                      <input
                        type="checkbox"
                        data-testid="v2-shell-enabled"
                        checked={shellEnabled}
                        onChange={(e) => setShellEnabled(e.target.checked)}
                      />{' '}
                      Enable corrective shell
                    </label>
                  </div>
                  <div className="v2-adjust-row">
                    <label
                      title="Deep heel cup walls (posterior + medial + lateral heel). Turn off per prescription (e.g. metatarsal-only pads)."
                    >
                      <input
                        type="checkbox"
                        data-testid="v2-shell-heel-cup"
                        checked={shellHeelCup}
                        disabled={!shellEnabled}
                        onChange={(e) => setShellHeelCup(e.target.checked)}
                      />{' '}
                      Heel cup
                    </label>
                  </div>
                  <div className="v2-adjust-row">
                    <label title="Overall rim height scale (0.0–2.0). 1.0 = clinical baseline.">
                      Rim scale:
                    </label>
                    <input
                      type="number"
                      step="0.05"
                      min={0}
                      max={2}
                      data-testid="v2-shell-rim-scale"
                      value={shellRimScale}
                      disabled={!shellEnabled}
                      onChange={(e) => setShellRimScale(Number(e.target.value))}
                    />
                  </div>
                  <div className="v2-adjust-row">
                    <label title="Arch dome height in mm (0–20).">
                      Arch dome (mm):
                    </label>
                    <input
                      type="number"
                      step="0.5"
                      min={0}
                      max={20}
                      data-testid="v2-shell-arch-dome"
                      value={shellArchDome}
                      disabled={!shellEnabled}
                      onChange={(e) => setShellArchDome(Number(e.target.value))}
                    />
                  </div>
                  <div className="v2-adjust-row">
                    <label title="Medial wall height in mm (0–20).">
                      Medial wall (mm):
                    </label>
                    <input
                      type="number"
                      step="0.5"
                      min={0}
                      max={20}
                      data-testid="v2-shell-medial-wall"
                      value={shellMedialWall}
                      disabled={!shellEnabled}
                      onChange={(e) => setShellMedialWall(Number(e.target.value))}
                    />
                  </div>
                  <div className="v2-adjust-row">
                    <label title="Posterior heel support raise in mm (0–15). Clinical add-on.">
                      Heel support (mm):
                    </label>
                    <input
                      type="number"
                      step="0.5"
                      min={0}
                      max={15}
                      data-testid="v2-shell-heel-support"
                      value={shellHeelSupport}
                      disabled={!shellEnabled}
                      onChange={(e) => setShellHeelSupport(Number(e.target.value))}
                    />
                  </div>
                </div>
              </details>

              {/* Advanced refinement (V2 shell-rebuild) — collapsed by default */}
              <details className="v2-refine-advanced" style={{ marginTop: 12 }}>
                <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
                  Advanced refinement (V2)
                </summary>
                <div className="v2-adjust-controls" style={{ marginTop: 8 }}>
                  <div className="v2-adjust-row">
                    <label title="Master switch — when off, the legacy raw mesh is persisted as-is.">
                      <input
                        type="checkbox"
                        checked={refineEnabled}
                        onChange={(e) => setRefineEnabled(e.target.checked)}
                      />{' '}
                      Enable refine pass
                    </label>
                  </div>
                  <div className="v2-adjust-row">
                    <label title="Interior grid step in mm. Smaller = higher resolution but slower.">
                      Grid step (mm):
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min={0.4}
                      max={2.0}
                      value={refineGridStep}
                      disabled={!refineEnabled}
                      onChange={(e) => setRefineGridStep(Number(e.target.value))}
                    />
                  </div>
                  <div className="v2-adjust-row">
                    <label title="Shell thickness offset between top and bottom surfaces.">
                      Thickness (mm):
                    </label>
                    <input
                      type="number"
                      step="0.5"
                      min={2}
                      max={6}
                      value={refineThickness}
                      disabled={!refineEnabled}
                      onChange={(e) => setRefineThickness(Number(e.target.value))}
                    />
                  </div>
                  <div className="v2-adjust-row">
                    <label title="Rim fillet radius. 0 disables fillet.">
                      Rim fillet (mm):
                    </label>
                    <input
                      type="number"
                      step="0.5"
                      min={0}
                      max={4}
                      value={refineRimFillet}
                      disabled={!refineEnabled}
                      onChange={(e) => setRefineRimFillet(Number(e.target.value))}
                    />
                  </div>
                  <div className="v2-adjust-row">
                    <label title="Concave-hull alpha ratio for rim extraction. Higher = smoother rim, may lose detail.">
                      Hull ratio:
                    </label>
                    <input
                      type="number"
                      step="0.05"
                      min={0.1}
                      max={0.6}
                      value={refineHullRatio}
                      disabled={!refineEnabled}
                      onChange={(e) => setRefineHullRatio(Number(e.target.value))}
                    />
                  </div>
                  <div className="v2-adjust-row">
                    <label title="Force bottom surface flat (z = min - thickness).">
                      <input
                        type="checkbox"
                        checked={refineBottomFlat}
                        disabled={!refineEnabled}
                        onChange={(e) => setRefineBottomFlat(e.target.checked)}
                      />{' '}
                      Flat bottom
                    </label>
                  </div>
                </div>
              </details>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => { setV2Step('template'); setAlignResult(null) }}>Back</button>
              <button
                className="btn-primary"
                onClick={handleV2Generate}
              >
                Confirm & Generate Insole
              </button>
            </div>
          </>
        )}

        {/* Step 3: Generating (shown briefly before LoadingStatus takes over) */}
        {v2Step === 'generating' && (
          <div className="modal-body">
            <p>Generation in progress... See loading overlay.</p>
            {alignResult?.session_id && (
              <button
                className="btn-cancel"
                style={{ marginTop: 12 }}
                onClick={async () => {
                  try {
                    const { backendService } = await import('../../services/backendService')
                    await backendService.cancelV2Session(alignResult.session_id)
                  } catch (e) {
                    console.error('cancelV2Session failed', e)
                  }
                  setV2Step('align-preview')
                }}
              >
                Cancel Generation
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )

  if (usePortal && pinToContainer) {
    return ReactDOM.createPortal(dialogTree, pinToContainer)
  }
  return dialogTree
}

// ==================== V2 TEMPLATE PICKER ====================
// Three dropdowns (size · gender · type) replace the old long template list.
// "Available" templates are derived from the live template list. Gender is
// inferred from the template name (Male / Female / Pediatric); template
// records have no explicit gender column.

const TYPE_LABEL: Record<string, string> = {
  full_length: 'Full Length',
  three_quarter: '3/4 Length',
  metatarsal: 'Metatarsal',
  heel_cup: 'Heel Cup',
}

function inferGender(name: string): 'male' | 'female' | 'pediatric' | 'unknown' {
  const n = name.toLowerCase()
  if (n.includes('female') || n.includes('woman') || n.includes('women')) return 'female'
  if (n.includes('pediatric') || n.includes('child') || n.includes('kid')) return 'pediatric'
  if (n.includes('male') || n.includes('man') || n.includes('men')) return 'male'
  return 'unknown'
}

/** Parse "EU 38" / "EU38" / "EU 38.5" out of the template name. */
function inferSize(name: string, fallback?: string): string {
  if (fallback) return fallback
  const m = (name || '').match(/EU\s*([0-9]+(?:\.[0-9]+)?)/i)
  return m ? m[1] : ''
}

interface V2TemplatePickerProps {
  templates: TemplateItem[]
  selectedTemplateId: string | null
  onSelect: (id: string) => void
  onSelectionChange?: (
    selection:
      | {
          templateId: string
          templateName: string
          size: string
          gender: string
          type: string
        }
      | null
  ) => void
}

const V2TemplatePicker: React.FC<V2TemplatePickerProps> = ({
  templates,
  selectedTemplateId,
  onSelect,
  onSelectionChange,
}) => {
  const decorated = useMemo(
    () =>
      templates.map(t => ({
        ...t,
        _gender: inferGender(t.name || ''),
        _size: inferSize(t.name || '', t.size?.toString()),
        _type: (t.type || '').toString(),
      })),
    [templates]
  )

  // Default to the most common combo if available
  const sizes = useMemo(
    () =>
      Array.from(new Set(decorated.map(t => t._size).filter(Boolean))).sort(
        (a, b) => parseFloat(a) - parseFloat(b)
      ),
    [decorated]
  )
  const genders = useMemo(
    () =>
      Array.from(
        new Set(decorated.map(t => t._gender).filter(g => g !== 'unknown'))
      ).sort(),
    [decorated]
  )
  const types = useMemo(
    () => Array.from(new Set(decorated.map(t => t._type).filter(Boolean))).sort(),
    [decorated]
  )

  const [size, setSize] = useState<string>(sizes.includes('44') ? '44' : sizes[0] || '')
  const [gender, setGender] = useState<string>(
    genders.includes('male') ? 'male' : genders[0] || ''
  )
  const [type, setType] = useState<string>(
    types.includes('three_quarter') ? 'three_quarter' : types[0] || ''
  )

  // Templates load asynchronously — once `sizes` arrives, pick a sensible default.
  useEffect(() => {
    if (!size && sizes.length > 0) {
      setSize(sizes.includes('44') ? '44' : sizes[Math.floor(sizes.length / 2)])
    }
  }, [sizes, size])
  useEffect(() => {
    if (!gender && genders.length > 0) {
      setGender(genders.includes('male') ? 'male' : genders[0])
    }
  }, [genders, gender])
  useEffect(() => {
    if (!type && types.length > 0) {
      setType(types.includes('three_quarter') ? 'three_quarter' : types[0])
    }
  }, [types, type])

  // Resolve the matching template, if any
  const matched = useMemo(
    () =>
      decorated.find(
        t => t._size === size && t._gender === gender && t._type === type
      ) || null,
    [decorated, size, gender, type]
  )

  // Auto-select matched template
  useEffect(() => {
    if (matched && matched.id !== selectedTemplateId) {
      onSelect(matched.id)
    }
  }, [matched, selectedTemplateId, onSelect])

  // Bubble selection metadata up so GirasolesView can render the
  // Insole Characteristics card and an "Insole Cut" row in the
  // G-Object Manager. Use a ref to avoid retriggering on parent
  // identity changes.
  const onSelectionChangeRef = React.useRef(onSelectionChange)
  useEffect(() => {
    onSelectionChangeRef.current = onSelectionChange
  }, [onSelectionChange])
  useEffect(() => {
    const cb = onSelectionChangeRef.current
    if (!cb) return
    if (matched) {
      cb({
        templateId: matched.id,
        templateName: matched.name || `EU ${size} ${gender} ${type}`,
        size,
        gender,
        type,
      })
    } else {
      cb(null)
    }
  }, [matched, size, gender, type])

  const isAvailable = (s: string, g: string, ty: string): boolean =>
    decorated.some(t => t._size === s && t._gender === g && t._type === ty)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <p style={{ fontSize: 13, color: '#475569', margin: 0 }}>
        Pick the size, gender, and insole type for this patient. The system
        will pick the matching template automatically.
      </p>

      <div className="form-row">
        <div className="form-group">
          <label>EU Size</label>
          <select
            data-testid="v2-size-select"
            value={size}
            onChange={(e) => setSize(e.target.value)}
          >
            {sizes.map(s => (
              <option key={s} value={s}>EU {s}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Gender</label>
          <select
            data-testid="v2-gender-select"
            value={gender}
            onChange={(e) => setGender(e.target.value)}
          >
            {genders.map(g => (
              <option key={g} value={g}>
                {g.charAt(0).toUpperCase() + g.slice(1)}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Insole Type</label>
          <select
            data-testid="v2-type-select"
            value={type}
            onChange={(e) => setType(e.target.value)}
          >
            {types.map(ty => (
              <option key={ty} value={ty}>
                {TYPE_LABEL[ty] || ty}
                {!isAvailable(size, gender, ty) ? '  (unavailable)' : ''}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div
        data-testid="v2-template-status"
        style={{
          padding: '10px 14px',
          borderRadius: 6,
          background: matched ? '#ecfdf5' : '#fef2f2',
          border: `1px solid ${matched ? '#86efac' : '#fca5a5'}`,
          color: matched ? '#065f46' : '#991b1b',
          fontSize: 13,
        }}
      >
        {matched ? (
          <>
            <strong>Available:</strong> {matched.name}
          </>
        ) : (
          <>
            <strong>Not available.</strong> No template exists for EU {size} ·{' '}
            {gender} · {TYPE_LABEL[type] || type}. Try a different combination.
          </>
        )}
      </div>
    </div>
  )
}


